"use client";
import { apiSpecificOffers } from '@/apis/offers';
import { apiExpiredOffers } from '@/apis/user';
import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl } from '@/constants/hooks';
import dynamic from 'next/dynamic';

const Banner = dynamic(() => import('./Banner'), { ssr: false });
const OffersListView = dynamic(() => import('./OffersListView'), { ssr: false });

interface Props {
    initialOffers: OffersOffer[];
    companyId: string;
    merchantId: string;
    awaited_p_id: string;
    mer_slug_type: string;
    mer_slug: string;
    offerBanner: OffersOffer[];
    domain: string;
    merchantName: string;
    pagination: PaginationType;
    ads_campaign: boolean;
}

const LazyLoadingOffers = ({ initialOffers, companyId, merchantId, awaited_p_id, mer_slug_type, mer_slug, offerBanner, domain, merchantName, pagination, ads_campaign }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>(initialOffers);
    const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef<IntersectionObserver | null>(null);
    const [filteredOfferBanners, setFilteredOfferBanners] = useState<OffersOffer[]>([]);
    const bannerDisplayAfter = 2;

    useEffect(() => { setOffers(initialOffers); fetchExpiredOffers(); }, [initialOffers]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        if (page === pagination?.last_page) { setHasMore(false); return; }
        setLoading(true);
        try {
            const res = await apiSpecificOffers(merchantId, companyId, page + 1);
            if (res.data?.offers.length > 0) { setOffers(prev => [...prev, ...res.data.offers]); setPage(p => p + 1); }
            else setHasMore(false);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    const fetchExpiredOffers = async () => {
        try {
            const res = await apiExpiredOffers(merchantId, companyId);
            setExpiredOffers(res.data?.offers || []);
        } catch { /* silent */ }
    };

    const observerCallback = useCallback((node: any) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) loadMore();
        }, { threshold: 0.1 });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, loadMore]);

    useEffect(() => {
        if (offerBanner?.length > 0) setFilteredOfferBanners(filterOfferBanners(offerBanner, 300, 600, 50, 150));
    }, [offerBanner]);

    return (
        <>
            {/* Active offers */}
            <div className="flex flex-col gap-4">
                {offers.map((offer, index) =>
                    offer.status === 1 && offer.offer.status === 1 ? (
                        <React.Fragment key={offer.id}>
                            <div ref={index === offers.length - 1 ? observerCallback : undefined}>
                                <OffersListView
                                    product={offer} companyId={companyId} awaited_p_id={awaited_p_id}
                                    mer_slug_type={mer_slug_type} mer_slug={mer_slug}
                                    domain={domain} ads_campaign={ads_campaign} />
                            </div>
                            {(index + 1) % bannerDisplayAfter === 0 &&
                                filteredOfferBanners[Math.floor(index / bannerDisplayAfter)] && (() => {
                                    const banner = filteredOfferBanners[Math.floor(index / bannerDisplayAfter)];
                                    const dimension = getBannerDimensions(banner);
                                    return (
                                        <div className="w-full flex justify-center py-2" key={`banner-${index}`}>
                                            <Banner data={banner} height={dimension?.height} width={dimension?.width}
                                                offerLink={null} domain={domain} mer_slug={mer_slug} slug_type={mer_slug_type} />
                                        </div>
                                    );
                                })()}
                        </React.Fragment>
                    ) : null
                )}

                {/* Loading spinner */}
                {loading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="flex items-center gap-3">
                            <span className="w-5 h-5 border-2 border-[#7AFC20] border-t-transparent rounded-full animate-spin" />
                            <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: 'rgba(8,28,17,0.4)' }}>
                                Loading more...
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Expired offers */}
            {expiredOffers?.length > 0 && (
                <div className="mt-10">
                    {/* Expired header */}
                    <div className="flex items-center gap-4 mb-6 pb-4" style={{ borderBottom: '1px solid rgba(8,28,17,0.08)' }}>
                        <div
                            className="px-3 py-1 text-[9px] font-black uppercase tracking-widest"
                            style={{
                                background: 'rgba(8,28,17,0.06)',
                                border: '1px solid rgba(8,28,17,0.12)',
                                color: 'rgba(8,28,17,0.4)',
                                clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                            }}
                        >
                            May Still Work
                        </div>
                        <h3 className="text-base font-black m-0" style={{ color: '#081C11' }}>
                            Old deals for <span style={{ color: '#7AFC20' }}>{merchantName}</span>
                        </h3>
                    </div>

                    <div className="flex flex-col gap-4 opacity-70">
                        {expiredOffers.slice(0, 10).map((item, i) => (
                            <OffersListView key={i} product={item} companyId={companyId}
                                awaited_p_id={awaited_p_id} mer_slug_type={mer_slug_type}
                                mer_slug={mer_slug} domain={domain} ads_campaign={ads_campaign} />
                        ))}
                    </div>
                </div>
            )}

            <script type="application/ld+json" id="AggregateOffer"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "AggregateOffer",
                        "name": `${merchantName} Coupons & Deals`,
                        "description": `Active coupons and deals for ${merchantName}.`,
                        "offerCount": offers.length,
                        "offers": offers.map(o => ({
                            "@type": "Offer",
                            "name": o?.offer?.offer_title,
                            "description": o?.offer?.offer_detail,
                            "url": getBaseImageUrl(`${domain}`, `${o.offer?.url}`, ""),
                        }))
                    })
                }}
            />
        </>
    );
};

export default LazyLoadingOffers;
